import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useUpdateCouponMutation } from "Api/Mutations/Coupons/useUpdateCouponMutation";
import { DateFormInput } from "Components/Shared/Inputs/Form/DateFormInput";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { TextInput } from "Components/Shared/Inputs/TextInput";
import type { CouponDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const RESOURCES = Resources.Admin.Coupons;

const getEditCouponSchema = (minStock: number) =>
  z.object({
    expirationDate: z.string().min(1),
    initialStock: z.number().int().min(minStock),
  });

type EditCouponFormData = z.infer<ReturnType<typeof getEditCouponSchema>>;

type Props = {
  coupon: CouponDto;
  isOpen: boolean;
  onClose: () => void;
};

export const EditCouponDialog = ({ coupon, isOpen, onClose }: Props) => {
  const { t } = useResource();
  const { couponID, code, initialStock, currentStock, expirationDate } = coupon;

  const usedCount = initialStock - currentStock;

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<EditCouponFormData>({
    resolver: zodResolver(getEditCouponSchema(usedCount)),
    defaultValues: {
      expirationDate: new Date(expirationDate).toISOString().split("T")[0],
      initialStock,
    },
  });

  const { mutate: updateCoupon, isPending } = useUpdateCouponMutation({
    onSuccess: () => {
      toast.success(t(RESOURCES.Messages.UpdateSuccess));
      onClose();
    },
    onError: () => {
      toast.error(t(RESOURCES.Messages.UpdateError));
    },
  });

  const onSubmit = ({ expirationDate, initialStock }: EditCouponFormData) =>
    updateCoupon({
      couponID,
      data: {
        expirationDate: new Date(expirationDate).toISOString(),
        initialStock,
      },
    });

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {t(RESOURCES.Form.EditTitleWithCode, { code })}
        </DialogTitle>
        <DialogContent>
          <Stack gap={1}>
            <TextInput
              name="code"
              label={t(RESOURCES.Table.Code)}
              value={code}
              disabled
            />
            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              <Box flex={1}>
                <DateFormInput
                  control={control}
                  name="expirationDate"
                  label={t(RESOURCES.Form.Expiration)}
                />
              </Box>
              <Box flex={1}>
                <FormInput
                  control={control}
                  name="initialStock"
                  label={t(RESOURCES.Form.Stock)}
                  type="number"
                  min={usedCount}
                  helperText={t(RESOURCES.Form.StockHelp, { min: usedCount })}
                />
              </Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t(RESOURCES.Form.Cancel)}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isPending || !isValid}
          >
            {t(RESOURCES.Form.Save)}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
